import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  input,
  model,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { APP_CONSTANTS } from '@core/constants';
import { StringUtil, toFormControl } from '@shared/utils';

@Component({
  selector: 'app-rating',
  imports: [ReactiveFormsModule, NgTemplateOutlet],
  templateUrl: './rating.component.html',
  styleUrl: './rating.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Rating implements OnChanges {
  /** Variable que contiene los metodos de la libreria math para operaciones matematicás */
  protected Math = Math;
  /** Variable que define si se habilita o deshabilita la calificación*/
  readonly disabled = input<boolean>(false);
  /** Variable que contiene la calificacion actual*/
  readonly qualification = model<number>(0);
  /** Variable que determinara el estilo de la calificación, por defecto su valor es false*/
  readonly box = input<boolean>(false);

  readonly showCard = input<boolean>(false);

  /** Variable que determinara si se muestra o no el numero de la calificacion, por defecto su valor es true*/
  readonly showRatingNumber = input<boolean>(true);
  /** Variable que determinara un estilo para la calificacion segun su estado, por defecto su valor es false*/
  readonly gray = input<boolean>(false);
  /** Variable que determinara un estilo para la calificacion segun su estado, por defecto su valor es false */
  readonly marginAuto = input<boolean>(false);
  /** Variable que contendra informacion de tipo html para un bóton*/
  readonly buttonTemplate = input<TemplateRef<any> | null>(null);
  /** Variable que contendra el tamaño de los iconos de la calificación, por defecto su valor es '20px'*/
  readonly size = input<string>('20px');
  /** Array de string, por defecto su valor es vacio*/
  protected readonly randomStrings = signal<string[]>([]);
  /** HostBindinig le permite establecer propiedades en el elemento o componente, en este caso definir clases Css sobre el mismo */
  @HostBinding('class') get margin() {
    return this.marginAuto() ? 'm-auto' : 'item-rating';
  }
  /** Variable que contendra los controls para la calificación, estos guardan el valor de la calificacion*/
  protected readonly ratingControls = signal<AbstractControl[]>([]);
  /** Rating currently previewed while the pointer is over the stars. */
  protected readonly hoveredRating = signal(0);

  protected readonly isButtonTemplate = computed<boolean>(() => !!this.buttonTemplate());

  protected readonly toFormControl = toFormControl;

  /**
   * Iniciliza el parametro fb y llama o ejecuta el metodo this.createRatings()
   * @param fb Parametro que tendra las capacidades de un Formbuilder
   */
  constructor() {
    this.createRatings();
  }

  /**
   * Hook de angular que detecta los cambios, una vez ha ocurrido uno pasa a actualizar la calificacion o dejarla con valores por defecto.
   * */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['qualification']) {
      if (this.qualification() && this.ratingControls()) {
        this.setRating();
      } else if (this.ratingControls) {
        this.ratingControls().forEach((r) => r.setValue(false));
      }
    }
  }

  /**
   * Este metodo es el encargado de crear los control's para que el usuario pueda realizar calificaciones o editarlas, a su vez
   * le agrega un nombre haciendo uso de randomString y poder identificarlos.
   */
  private createRatings(): void {
    this.ratingControls.set([]);
    for (let i = 0; i < APP_CONSTANTS.MAX_RATING; i++) {
      this.ratingControls.update((prev) => [...prev, new FormControl(false)]);
      this.randomStrings.update((prev) => [...prev, StringUtil.randomString(10)]);
    }
  }

  /**
   * metodo que envia la calificación, este se activa una vez el usuario ha calificado o actualizado la misma.
   * @param qualification Parametro que recibe la calificacion hehca por un usuario
   */
  protected setRating(qualification?: number): void {
    this.createRatings();
    // `model()` ya emite `qualificationChange` al asignar el nuevo valor.
    if (qualification) {
      this.qualification.set(qualification);
    }
    for (let i = 0; i < Math.abs(this.qualification()); i++) {
      if (this.ratingControls()[i]) {
        this.ratingControls.update((controls) =>
          controls.map((control, index) => {
            if (index === i) {
              control.setValue(i + 1);
              return control;
            }
            return control;
          }),
        );
      }
    }
  }

  /**
   * Metodo que hae un reset a la calificacion y les da un valor de false a todas.
   */
  protected resetRatings(): void {
    for (let i = 0; i < Math.abs(this.qualification()); i++) {
      this.ratingControls.update((controls) =>
        controls.map((control, index) => {
          if (index === i && control.value) {
            control.reset(false);
            return control;
          }
          return control;
        }),
      );
    }
  }

  protected setHoveredRating(value: number): void {
    if (!this.disabled()) this.hoveredRating.set(value);
  }

  protected clearHoveredRating(): void {
    this.hoveredRating.set(0);
  }
}
